/**
 * PLT-7100 — the rows of a supplier import that were not created, as a CSV the user downloads from
 * the review card: each row as the card shows it, with the reason the card gives.
 *
 * Pure, so it can be spec'd. The card works out each row's reason, in its own words.
 */
import type { SupplierImportReviewRow } from '@upright/common/mcp-payloads/supplier-import-review';

export interface NotCreatedRow {
  row: SupplierImportReviewRow;
  reason: string;
}

export function buildNotCreatedCsv(rows: NotCreatedRow[], spendCurrency: string): string {
  const header = ['Row', 'Supplier', 'Website', 'Internal ID', `Annual spend (${spendCurrency})`, 'Reason'];
  const lines = rows.map(({ row, reason }) => [
    String(row.rowNumber),
    row.name ?? '',
    row.website ?? '',
    row.internalId ?? '',
    // The amount when it was read, else the cell as the file had it, so a spend left out still shows.
    row.spendAmount === null ? (row.spendAsInFile ?? '') : String(row.spendAmount),
    reason,
  ]);
  return `${[header, ...lines].map(cells => cells.map(csvCell).join(',')).join('\r\n')}\r\n`;
}

/**
 * Quoted when it holds a comma, a quote or a line break. The names, websites and IDs come from an
 * uploaded file, so a cell a spreadsheet would run as a formula gets an apostrophe in front.
 */
function csvCell(value: string): string {
  const inert = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(inert) ? `"${inert.replace(/"/g, '""')}"` : inert;
}

/** Named after the uploaded file, so the two sit together in a downloads folder. */
export function notCreatedCsvFilename(filename: string | null): string {
  const base = filename === null || filename === '' ? 'suppliers' : filename.replace(/\.[^.]+$/, '');
  return `${base} - not created.csv`;
}
